<template>
    <Popup v-model=ManualJournalPopUpStatus title="Post Manual Journal Entry" hasClose>
        <template #contents>
            <div class="flex-col-centered">
               <br>
            </div>
            <div  class="top-content">
                <form @submit.prevent="PostManualJournalEntry()">
                    
                    <div class="row">
                        <label for="journalReference">Journal Reference:</label>
                        <span id="journalReference"> {{ previewData?.JournalReference }} </span>
                    </div>
                    
                    <div class="row">
                        <label for="accountingDate"> Accounting Date:</label>
                        <span id="accountingDate"> {{ previewData?.AccountingDate }} </span>
                    </div>
                    
                    <div class="row">
                        <label for="headerDescription">Header Description:</label>
                        <span id="headerDescription"> {{ previewData?.HeaderDescription }} </span>
                    </div>

                    <div class="row">
                        <label for="headerDescription">Owner ID:</label>
                        <span id="headerDescription"> {{ previewData?.OwnerID }} </span>
                    </div>
                    
                    <div class="row">
                        <span style="margin-top: 15px" class="uderscore" for="market">Line Items :</span>
                    </div>
                    
                    <div style="padding-bottom: 10px" >
                        <Table 
                        :Rows=previewTable
                        :Cols=cols
                        :Labels=labels
                        :SummaryCols="SummaryCols"
                        :SummaryValues="SummaryValues"
                        ref="table"
                        >
                            <template #Description="props">
                                <button type="button" class="overflow-text"> {{ props.value.Description }}</button>
                            </template>
                            <template #Account="props">
                                <span style="display: flex;"> {{ formatEnumValue(props.value.Account) }}</span>
                            </template>
                        </Table>
                    </div>

                    <div class="debit-credit-mismatch" v-if="totalCredit != totalDebit">
                        Debit and credit amounts must be equal
                    </div>

                    <div style="grid-column: span 2; display: flex; justify-content: center; gap: 10px; padding-top: 10px">
                        <button 
                        class="submit-btn line-item-btn" 
                        :disabled="totalCredit != totalDebit"
                        :title="totalCredit != totalDebit? 'Total debit must equal total credit' : 'Submit Manual Journal'">
                            Submit 
                        </button>
                    </div>
                </form>
            </div>
        </template>
    </Popup>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import "@vuepic/vue-datepicker/dist/main.css";
import Popup from "./Popup.vue";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";
import useSpinnerPopup from "@/services/composables/SpinnerPopup";
import { manualJournal} from "@/services/network";
import IManualJournal from "@/Interfaces/Journaling/ManualJournal/IManualJournal";
import Table from "@/components/TablePersistentChecking.vue";
import { DisplayableKeys } from "@/services/SorterService";
import IManualJournalPreviewTable from "@/Interfaces/Journaling/ManualJournal/IPostManualJournalPreviewTable";
import IManualJournalBody from "@/Interfaces/Journaling/ManualJournal/IManualJournalBody";
import { authService } from "@/auth/authSingleton";

const postRequestPayload = ref<IManualJournalBody>()
const labels = ["Account", "Description", "Debit Amount", "Credit Amount", "Tax Code", "Tax Return"]
const working = ref(false);
const cols: DisplayableKeys<IManualJournalPreviewTable>[] = [
    "Account",
    "Description", 
    "DebitAmount",
    "CreditAmount", 
    "TaxCode", 
    "TaxReturn"
];
const SummaryCols: DisplayableKeys<IManualJournalPreviewTable>[] = ["DebitAmount", "CreditAmount"]
    const totalDebit = computed(() =>
  Math.round(props.previewTable.reduce((sum, item) => sum + (Number(item.DebitAmount) || 0), 0) * 100) / 100
);

const totalCredit = computed(() =>
  Math.round(props.previewTable.reduce((sum, item) => sum + (Number(item.CreditAmount) || 0), 0) * 100) / 100
);
const SummaryValues = computed(() => [totalDebit.value, totalCredit.value]);

const ManualJournalPopUpStatus = computed({
    get() {
        return props.modelValue
    },
    set(val) {
        emits('update:modelValue', val);
    }
});
const props = defineProps<{
    modelValue?: boolean;
    previewData: IManualJournal;
    previewTable: IManualJournalPreviewTable[]

}>();
const emits = defineEmits<{
    (e: 'update:modelValue', val?: boolean);
    (e: 'confirm');
}>();

function formatEnumValue (string: string){
    return string.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (word) => word.toUpperCase());
};
function closeChild(){
    ManualJournalPopUpStatus.value = false;
}
async function PostManualJournalEntry(){
    
    postRequestPayload.value = {
        ...props.previewData, 
        LineItems: props.previewTable, 
        UserName: authService.getEmailAddress(),
        JournalReference: props.previewData.JournalReference == "Auto Generated" ? "" : props.previewData.JournalReference
    }
    if(working.value) return;    
    const closeSpinner = useSpinnerPopup().show();
    try{
        working.value=true
        if(postRequestPayload.value == undefined){
            throw new Error("Post request payload is not defined")
        }else{
            await manualJournal.post(postRequestPayload.value)
            useNotifierWithErrFormatter().success({ msg: "Manual Journal Entry Successfully Registered." });
            emits("confirm");
            closeChild()
        }
    }catch(error){
        useNotifierWithErrFormatter().error({error});
    }
    closeSpinner()
    working.value=false
}
</script>
<style scoped lang="scss">

.grid-container {
    display: grid;
    grid-template-columns: auto auto;
    gap: 0.5rem;
}
.submit-btn {
    margin-top: 1rem;
    cursor: pointer;
    border-width: 0px;
    border-radius: var(--buttons-border-radius);
    background-color: var(--primary-color-lighter);
    padding: 10px;
    color: blue;
    font-size: larger;
    font-weight: bold;
    &:hover {
        color: white;
        background-color: var(--primary-color);
    }
}
.line-item-btn {
    font-size: 16px;
    width: 200px;
    margin-top: 2px;
}
.overflow-text {
    white-space: nowrap;
    overflow: hidden; 
    text-overflow: ellipsis;
    max-width: 7vw;
    background: none;
    border: none;
    padding: 0;
    text-decoration: none;
    cursor: text;
    color: var(--primary-color-dark);
    font-weight: bold;
    &:hover{
        text-decoration: none;
        color: var(--primary-color-dark);
        cursor: text;
        font-weight: bold;
    }
}
.row {
    display: flex;
    flex-direction: row;
    gap: 10px;
    padding-bottom: 10px;
}
.row label{
    text-align: left;
    width: 20%;
}
.row span{
    text-align: left;
    width: 80%;
}
.submit-btn:disabled {
    background-color: var(--light-grey);
    color: #b6b6b6;
    cursor: not-allowed;
}
.debit-credit-mismatch{
    color: red;
}
</style>